import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { ProductCard } from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

export default function Products() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [priceMax, setPriceMax] = useState(200);
  const [sort, setSort] = useState("");

  const category = params.get("category") || "";
  const q = params.get("q") || "";
  const flash = params.get("flash");
  const country = params.get("country") || "";

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);

  const load = useCallback(() => {
    const query = new URLSearchParams();
    if (q) query.set("q", q);
    if (category) query.set("category", category);
    if (country) query.set("country", country);
    if (flash) query.set("flash_sale", "true");
    if (priceMax < 200) query.set("max_price", priceMax);
    if (sort) query.set("sort", sort);
    api.get(`/products?${query.toString()}`).then((r) => setProducts(r.data));
  }, [q, category, country, flash, priceMax, sort]);

  useEffect(() => { load(); }, [load]);

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };

  const countries = ["Nigeria", "France", "Italy", "United Kingdom", "United States", "Spain", "Germany", "Netherlands", "Belgium", "China"];

  const catLabel = cats.find((c) => c.slug === category)?.name;
  const seoTitle = q ? `Search: ${q}` : catLabel ? catLabel : flash ? "Flash Sales" : "All Products";
  const seoDesc = catLabel
    ? `Shop ${catLabel} — authentic African products on Xurray Mart, delivered across Europe.`
    : `Browse authentic African products on Xurray Mart — food, beauty, fashion and more, delivered across Europe.`;

  return (
    <Layout seo={{ title: seoTitle, description: seoDesc }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters */}
          <aside className="lg:w-64 shrink-0 space-y-6" data-testid="filters-panel">
            <div className="flex items-center gap-2 font-display font-bold text-lg"><SlidersHorizontal className="w-4 h-4" />{t("filters")}</div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("category")}</Label>
              <div className="mt-2 space-y-1">
                <button data-testid="cat-filter-all" onClick={() => setParam("category", "")} className={`block text-sm ${!category ? "text-primary font-semibold" : "text-muted-foreground hover:text-foreground"}`}>{t("all")}</button>
                {cats.map((c) => (
                  <button key={c.slug} data-testid={`cat-filter-${c.slug}`} onClick={() => setParam("category", c.slug)}
                    className={`block text-sm ${category === c.slug ? "text-primary font-semibold" : "text-muted-foreground hover:text-foreground"}`}>{c.name}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("country")}</Label>
              <Select value={country || "all"} onValueChange={(v) => setParam("country", v === "all" ? "" : v)}>
                <SelectTrigger data-testid="country-filter" className="mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allCountries")}</SelectItem>
                  {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("maxPrice")}: €{priceMax}</Label>
              <Slider data-testid="price-slider" value={[priceMax]} onValueChange={(v) => setPriceMax(v[0])} min={5} max={200} step={5} className="mt-3" />
            </div>
            <Button variant="outline" size="sm" className="w-full" data-testid="clear-filters" onClick={() => { setParams({}); setPriceMax(200); }}>{t("clearAll")}</Button>
          </aside>

          {/* Grid */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-display font-bold text-2xl">
                {q ? `${t("resultsFor")} "${q}"` : flash ? `⚡ ${t("flashSales")}` : category ? cats.find((c) => c.slug === category)?.name : t("allProducts")}
                <span className="text-sm text-muted-foreground font-normal ml-2">({products.length})</span>
              </h1>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-44" data-testid="sort-select"><SelectValue placeholder={t("sortBy")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">{t("newest")}</SelectItem>
                  <SelectItem value="price_asc">{t("priceLowHigh")}</SelectItem>
                  <SelectItem value="price_desc">{t("priceHighLow")}</SelectItem>
                  <SelectItem value="rating">{t("topRated")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {products.length === 0 ? (
              <p className="text-muted-foreground py-20 text-center">{t("noProducts")}</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
                {products.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
